import { motion } from "framer-motion";
import { IMG, BRAND, waLink } from "@/lib/brand";
import { Check, Globe2, HeartHandshake, ShieldCheck, Sparkles } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useT } from "@/lib/i18n";

export default function WhyUs() {
  const { t } = useT();
  const reasons = [
    { icon: Globe2, t: "why.r1.t", x: "why.r1.x" },
    { icon: ShieldCheck, t: "why.r2.t", x: "why.r2.x" },
    { icon: HeartHandshake, t: "why.r3.t", x: "why.r3.x" },
    { icon: Sparkles, t: "why.r4.t", x: "why.r4.x" },
  ];

  return (
    <section id="why" data-testid="why-section" className="relative py-24 md:py-32 bg-[#0B1733] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-gold/20">
              <img src={IMG.handshake} alt="Handshake with keys" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent" />
            </div>
            <div className="hidden sm:block absolute -bottom-8 -right-8 w-52 bg-navy-800 border border-gold/30 rounded-2xl p-5 shadow-2xl">
              <div className="text-xs tracking-widest text-gold uppercase mb-2">{t("why.langs")}</div>
              <div className="grid grid-cols-2 gap-2">
                {BRAND.languages.map((l) => (
                  <div key={l} className="text-sm text-white flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-gold shrink-0" />
                    {t(`lang.${l}`)}
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden sm:block absolute -top-6 -left-6 h-28 w-28 border border-gold/30 rounded-full" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="text-xs tracking-[0.3em] text-gold uppercase mb-4">{t("why.eyebrow")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
              {t("why.title.1")} <span className="italic text-gold">{t("why.title.2")}</span> {t("why.title.3")}
            </h2>
            <p className="text-slate-300/90 leading-relaxed mb-10 max-w-xl">{t("why.blurb")}</p>

            <div className="grid sm:grid-cols-2 gap-5">
              {reasons.map((r, i) => (
                <div
                  key={i}
                  data-testid={`reason-${i}`}
                  className="group bg-navy-800/60 border border-gold/15 rounded-2xl p-6 hover:border-gold/50 hover:bg-navy-800 transition-[color,background-color,border-color,box-shadow,transform]"
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gold/10 border border-gold/30 text-gold mb-4 group-hover:bg-gold group-hover:text-navy-900 transition-[color,background-color,border-color,box-shadow,transform]">
                    <r.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-serif text-xl text-white mb-2">{t(r.t)}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{t(r.x)}</p>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <a
                href={waLink(t("wa.default"))}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="why-whatsapp-cta"
                className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-navy-900 font-semibold px-7 py-3.5 rounded-full transition-[color,background-color,border-color,box-shadow,transform] shadow-[0_10px_40px_-10px_rgba(201,162,75,0.5)] hover:-translate-y-0.5"
              >
                <FaWhatsapp className="h-5 w-5" />
                {t("why.cta")}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
